import { action, makeObservable, observable, runInAction } from "mobx";
import UrgentSourcingAPI from "../api/UrgentSourcingAPI";
import { UrgentSourcingRaisedRequestFilters } from "../models/UrgentSourcing/UrgentSourcingRequestFilter";
import { UrgentSourcingProductFilters } from "../models/UrgentSourcing/UrgentSourcingProductFilter";
import {
  UrgentSourcingRaisedRequestForm,
  UrgentSourcingRaisedReqProductSection,
  UrgentSourcingAddProductForm,
  UrgentSourcingCatalogProductForm,
  UrgentSourcingRaisedRequestDetails,
  UrgentSourcingProductDetails,
  UrgentSourcingCategory,
  ProductImage,
} from "../models/UrgentSourcing/UrgentSourcing";
import dayjs from "dayjs";

export class UrgentSourcingStore {
  urgentSourcingAPI: UrgentSourcingAPI;
  urgentSourcingRaisedReqResponce: UrgentSourcingRaisedRequestDetails[];
  urgentSourcingProductsResponce: UrgentSourcingProductDetails[];
  urgentSourcingCategoriesResponse: UrgentSourcingCategory[];
  urgentSourcingCategoriesListResponse: UrgentSourcingCategory[];
  raisedRequestFormData: UrgentSourcingRaisedRequestForm;
  addProductFormData: UrgentSourcingAddProductForm;
  catalogProductFormData: UrgentSourcingCatalogProductForm;
  limit: number;
  total_pages: number;
  total_count: number;
  filterCount: number;
  isLoading: boolean;
  cancelReason: string;
  categoryName: string;
  isFetchingData: boolean;
  selectedDescription: string;
  selectedDescriptionType: "product" | "cancel" | null;
  imageUploadKey: number;
  isAddingNewCategory: boolean;
  isSavingCategory: boolean;
  newCategoryName: string;
  anchorEl: null | HTMLElement;
  selectedRequestId: number | null;
  hoveredProductId: number | null;
  openImageGalleryModal: boolean;
  selectedProductImages: ProductImage[];

  popupState: {
    openFilter: boolean;
    openRaiseRequest: boolean;
    openCancelProductPopup: boolean;
    openAddProductPopup: boolean;
    openDescriptionModal: boolean;
  };

  constructor() {
    this.urgentSourcingAPI = new UrgentSourcingAPI();
    this.urgentSourcingRaisedReqResponce = [];
    this.urgentSourcingProductsResponce = [];
    this.urgentSourcingCategoriesResponse = [];
    this.urgentSourcingCategoriesListResponse = [];
    this.limit = 0;
    this.total_count = 0;
    this.total_pages = 0;
    this.filterCount = 0;
    this.isLoading = false;
    this.cancelReason = "";
    this.categoryName = "";
    this.isFetchingData = false;
    this.imageUploadKey = 0;
    this.isAddingNewCategory = false;
    this.isSavingCategory = false;
    this.newCategoryName = "";
    this.anchorEl = null;
    this.selectedRequestId = null;
    this.hoveredProductId = null;
    this.openImageGalleryModal = false;
    this.selectedProductImages = [];
    this.selectedDescription = "";
    this.selectedDescriptionType = null;
    this.popupState = {
      openFilter: false,
      openRaiseRequest: false,
      openCancelProductPopup: false,
      openAddProductPopup: false,
      openDescriptionModal: false,
    };
    this.raisedRequestFormData = {
      leadId: "",
      companyName: "",
      requestDate: this.getCurrentDateTimeString(),
      products: [
        {
          productName: "",
          priority: -1,
          timeline: 0,
          quantity: 0,
          minPriceRange: 0,
          maxPriceRange: 0,
          link: "",
          description: "",
          imageUrls: undefined,
        },
      ],
    };
    this.addProductFormData = {
      productName: "",
      timeline: "",
      priceWithoutTax: null,
      fiftyQuantityPrice: null,
      gst: "0",
      priceWithGST: null,
      hsn: null,
      selectedCategory: null,
      description: null,
      vendorName: null,
      vendorPhoneNumber: null,
      uploadedImagesURL: [],
      uploadedImageUrls: [],
    };
    this.catalogProductFormData = {
      display: null,
      sellingPrice: null,
      description: null,
      categoryId: null,
      catalogueId: null,
    };

    makeObservable(this, {
      urgentSourcingRaisedReqResponce: observable,
      urgentSourcingProductsResponce: observable,
      urgentSourcingCategoriesResponse: observable,
      urgentSourcingCategoriesListResponse: observable,
      limit: observable,
      total_count: observable,
      total_pages: observable,
      raisedRequestFormData: observable,
      filterCount: observable,
      isLoading: observable,
      isFetchingData: observable,
      popupState: observable,
      selectedDescription: observable,
      selectedDescriptionType: observable,
      addProductFormData: observable,
      catalogProductFormData: observable,
      cancelReason: observable,
      categoryName: observable,
      imageUploadKey: observable,
      isAddingNewCategory: observable,
      isSavingCategory: observable,
      newCategoryName: observable,
      anchorEl: observable,
      selectedRequestId: observable,
      hoveredProductId: observable,
      openImageGalleryModal: observable,
      selectedProductImages: observable,
      getUrgentSourcingRequestListing: action,
      getUrgentSourcingProductsListing: action,
      getUrgentSourcingCategories: action,
      raisedRequest: action,
      updateRaisedReqFormField: action,
      updateRaisedReqProductField: action,
      addProductSection: action,
      removeProductSection: action,
      resetForm: action,
      resetRaisedReqForm: action,
      resetAddProductForm: action,
      resetCatalogProductForm: action,
      resetCancelReason: action,
      addCategory: action,
      setFilterCount: action,
      setIsLoading: action,
      setIsFetchingData: action,
      setPopupState: action,
      managePopup: action,
      approveRequest: action,
      cancelRequest: action,
      addCatalogProduct: action,
      updateCatalogProductField: action,
      updateAddProductField: action,
      setSelectedDescription: action,
      setCancelReason: action,
      incrementImageUploadKey: action,
      updateUploadedImageUrls: action,
      setNewCategoryName: action,
      handleAddNewCategory: action,
      handleCancelNewCategory: action,
      setAnchorEl: action,
      setHoveredProductId: action,
      setImageGalleryModalOpen: action,
      setSelectedProductImages: action,
    });
  }

  getUrgentSourcingRequestListing = async (
    filters: UrgentSourcingRaisedRequestFilters
  ) => {
    try {
      const response =
        await this.urgentSourcingAPI.getUrgentSourcingRequestListing(filters);
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
      this.urgentSourcingRaisedReqResponce = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getUrgentSourcingProductsListing = async (
    filters: UrgentSourcingProductFilters
  ) => {
    try {
      const response =
        await this.urgentSourcingAPI.getUrgentSourcingProductsListing(filters);
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
      this.urgentSourcingProductsResponce = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getUrgentSourcingCategories = async () => {
    try {
      const response =
        await this.urgentSourcingAPI.getUrgentSourcingCategories();
      this.urgentSourcingCategoriesResponse = response;
    } catch (error: any) {
      throw error;
    }
  };

  getUrgentSourcingCategoriesList = async () => {
    try {
      const response =
        await this.urgentSourcingAPI.getUrgentSourcingCategoriesList();
      this.urgentSourcingCategoriesListResponse = response;
    } catch (error: any) {
      throw error;
    }
  };

  raisedRequest = async (data: UrgentSourcingRaisedRequestForm) => {
    try {
      const formattedData = {
        ...data,
        requestDate: this.getCurrentDateTimeString(),
      };
      const response = await this.urgentSourcingAPI.raisedRequest(
        formattedData
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  approveRequest = async (addProductFormData: UrgentSourcingAddProductForm) => {
    try {
      const response = await this.urgentSourcingAPI.approveRequest(
        addProductFormData
      );
      if (response.success && response.result) {
        this.urgentSourcingRaisedReqResponce =
          this.urgentSourcingRaisedReqResponce.map((item) =>
            item.id === Number(addProductFormData.urgentSourcingRequestId)
              ? {
                  ...item,
                  status: response.result.status,
                  status_label: response.result.status_label,
                  product_catalog_creator_id:
                    response.result.product_catalog_creator_id,
                  product_catalog_creator_name:
                    response.result.product_catalog_creator_name,
                  product_catalog_created_date:
                    response.result.product_catalog_created_date,
                  tat1: response.result.tat1,
                }
              : item
          );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  cancelRequest = async (id: number) => {
    try {
      const response = await this.urgentSourcingAPI.cancelRequest(
        id,
        this.cancelReason
      );

      if (response.success && response.result) {
        this.urgentSourcingRaisedReqResponce =
          this.urgentSourcingRaisedReqResponce.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: response.result.status,
                  status_label: response.result.status_label,
                  cancel_reason: response.result.cancel_reason,
                  product_catalog_creator_id:
                    response.result.product_catalog_creator_id,
                  product_catalog_creator_name:
                    response.result.product_catalog_creator_name,
                }
              : item
          );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  addCatalogProduct = async (data: UrgentSourcingCatalogProductForm) => {
    try {
      const response = await this.urgentSourcingAPI.addCatalogProduct(data);
      if (response.success && response.result && data.catalogueId) {
        const productIndex = this.urgentSourcingProductsResponce.findIndex(
          (item) => item.id === data.catalogueId
        );
        if (productIndex !== -1) {
          this.urgentSourcingProductsResponce[
            productIndex
          ].is_admin_product = 1;
          this.urgentSourcingProductsResponce[productIndex].product_id =
            response.result;
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  addCategory = async (categoryName: string) => {
    try {
      const response = await this.urgentSourcingAPI.addCategory(categoryName);
      this.categoryName = response.result.result;
      this.urgentSourcingCategoriesResponse.push({
        id: Number(response.result.result),
        category_name: categoryName,
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  //from here to end of the file is the function to update the form fields

  setFilterCount = (count: number) => {
    this.filterCount = count;
  };

  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  resetCancelReason = () => {
    runInAction(() => {
      this.cancelReason = "";
    });
  };

  updateRaisedReqFormField = (
    field: keyof UrgentSourcingRaisedRequestForm,
    value: string
  ) => {
    if (field === "leadId" || field === "companyName") {
      runInAction(() => {
        this.raisedRequestFormData[field] = value;
      });
    }
  };

  updateRaisedReqProductField = (
    productIndex: number,
    field: keyof UrgentSourcingRaisedReqProductSection,
    value: string | number | null | File
  ) => {
    if (
      productIndex >= 0 &&
      productIndex < this.raisedRequestFormData.products.length
    ) {
      runInAction(() => {
        const product = this.raisedRequestFormData.products[productIndex];
        if (product) {
          Object.assign(product, { [field]: value || null });
        }
      });
    }
  };

  updateCatalogProductField = (
    field?: keyof UrgentSourcingCatalogProductForm,
    value?: string | number | null | string[] | File[] | undefined
  ) => {
    runInAction(() => {
      if (field) {
        Object.assign(this.catalogProductFormData, { [field]: value || null });
      }
    });
  };

  updateAddProductField = (
    field?: keyof UrgentSourcingAddProductForm,
    value?: string | number | null | string[] | File[] | undefined,
    requestId?: number | null
  ) => {
    runInAction(() => {
      if (field && value !== undefined) {
        if (field === "uploadedImagesURL" && Array.isArray(value)) {
          this.addProductFormData.uploadedImagesURL = value as string[];
        } else if (field === "uploadedImageUrls" && Array.isArray(value)) {
          this.addProductFormData.uploadedImageUrls = value as File[];
        } else if (
          field !== "uploadedImagesURL" &&
          field !== "uploadedImageUrls"
        ) {
          this.addProductFormData[field] = value as never;
        }
      }

      if (field === "fiftyQuantityPrice" || field === "gst") {
        const fiftyQuantityPrice =
          parseFloat(
            this.addProductFormData.fiftyQuantityPrice?.toString() || "0"
          ) || 0;
        const gstRate =
          parseFloat(this.addProductFormData.gst?.toString() || "0") || 0;

        if (fiftyQuantityPrice && gstRate >= 0) {
          const gstAmount = (fiftyQuantityPrice * gstRate) / 100;
          let priceWithGst = gstAmount + fiftyQuantityPrice;
          if (field === "fiftyQuantityPrice") {
            priceWithGst = Math.ceil(priceWithGst);
          }
          this.addProductFormData.priceWithGST = priceWithGst.toString();
        } else {
          this.addProductFormData.priceWithGST = "";
        }
      }
      if (requestId) {
        const newRequestId = requestId.toString();

        const requestData = this.urgentSourcingRaisedReqResponce.find(
          (item) => item.id === requestId
        );
        this.addProductFormData = {
          urgentSourcingRequestId: newRequestId,
          productName: requestData?.product_name || "",
          timeline: requestData?.timeline?.toString() || "",
          priceWithoutTax: null,
          fiftyQuantityPrice: null,
          gst: "0",
          priceWithGST: null,
          hsn: null,
          selectedCategory: null,
          description: null,
          vendorName: null,
          vendorPhoneNumber: null,
          uploadedImagesURL: [],
          uploadedImageUrls: [],
        };
      }
    });
  };

  addProductSection = () => {
    runInAction(() => {
      this.raisedRequestFormData.products.push({
        productName: "",
        priority: -1,
        timeline: 0,
        quantity: 0,
        minPriceRange: 0,
        maxPriceRange: 0,
        link: "",
        description: "",
        imageUrls: undefined,
      });
    });
  };

  removeProductSection = (productIndex: number) => {
    if (this.raisedRequestFormData?.products?.length > 1) {
      runInAction(() => {
        this.raisedRequestFormData.products?.splice(productIndex, 1);
      });
    }
  };

  resetForm = (formType: "raisedRequest" | "addProduct" | "catalogProduct") => {
    runInAction(() => {
      switch (formType) {
        case "raisedRequest":
          this.raisedRequestFormData = {
            leadId: "",
            companyName: "",
            requestDate: this.getCurrentDateTimeString(),
            products: [
              {
                productName: "",
                priority: -1,
                timeline: 0,
                quantity: 0,
                minPriceRange: 0,
                maxPriceRange: 0,
                link: "",
                description: "",
                imageUrls: undefined,
              },
            ],
          };
          this.imageUploadKey = 0;
          break;
        case "addProduct":
          this.addProductFormData = {
            urgentSourcingRequestId: undefined,
            productName: "",
            timeline: "",
            priceWithoutTax: null,
            fiftyQuantityPrice: null,
            gst: "0",
            priceWithGST: null,
            hsn: null,
            selectedCategory: null,
            description: null,
            vendorName: null,
            vendorPhoneNumber: null,
            uploadedImagesURL: [],
            uploadedImageUrls: [],
          };
          break;
        case "catalogProduct":
          this.catalogProductFormData = {
            display: null,
            sellingPrice: null,
            description: null,
            categoryId: null,
            catalogueId: null,
          };
          break;
      }
    });
  };
  resetRaisedReqForm = () => this.resetForm("raisedRequest");
  resetAddProductForm = () => this.resetForm("addProduct");
  resetCatalogProductForm = () => this.resetForm("catalogProduct");

  setCancelReason = (reason: string) => {
    this.cancelReason = reason;
  };

  setPopupState = (popup: keyof typeof this.popupState, value: boolean) => {
    this.popupState[popup] = value;
  };

  managePopup = (
    popup: keyof typeof this.popupState,
    action: "open" | "close",
    additionalData?: {
      description?: string;
      descriptionType?: "product" | "cancel";
    }
  ) => {
    runInAction(() => {
      if (action === "open") {
        this.popupState[popup] = true;

        if (popup === "openDescriptionModal" && additionalData?.description) {
          this.selectedDescription = additionalData.description;
          this.selectedDescriptionType = additionalData.descriptionType || null;
        }
      } else if (action === "close") {
        this.popupState[popup] = false;

        if (popup === "openCancelProductPopup") {
          this.resetCancelReason();
        } else if (popup === "openAddProductPopup") {
          this.resetAddProductForm();
        } else if (popup === "openDescriptionModal") {
          this.selectedDescription = "";
          this.selectedDescriptionType = null;
        }
      }
    });
  };
  incrementImageUploadKey = () => {
    this.imageUploadKey += 1;
  };

  setSelectedDescription = (description: string) => {
    this.selectedDescription = description;
  };

  updateUploadedImageUrls = (files: File[]) => {
    runInAction(() => {
      this.addProductFormData.uploadedImageUrls = files;
    });
  };

  getCurrentDateTimeString = (): string => {
    return dayjs().format("YYYY-MM-DD HH:mm:ss");
  };

  setIsFetchingData = (isFetching: boolean) => {
    this.isFetchingData = isFetching;
  };

  setNewCategoryName = (name: string) => {
    this.newCategoryName = name;
  };

  handleCancelNewCategory = () => {
    runInAction(() => {
      this.isAddingNewCategory = false;
      this.isSavingCategory = false;
      this.newCategoryName = "";
    });
  };

  handleAddNewCategory = () => {
    runInAction(() => {
      this.isAddingNewCategory = true;
      this.newCategoryName = "";
    });
  };

  setAnchorEl = (element: null | HTMLElement, requestId?: number | null) => {
    this.anchorEl = element;
    if (requestId !== undefined) {
      this.selectedRequestId = requestId;
    }
    if (element === null) {
      this.selectedRequestId = null;
    }
  };

  setHoveredProductId = (id: number | null) => {
    this.hoveredProductId = id;
  };

  setImageGalleryModalOpen = (isOpen: boolean) => {
    this.openImageGalleryModal = isOpen;
    if (!isOpen && this.selectedProductImages.length > 0) {
      this.selectedProductImages = [];
    }
  };

  setSelectedProductImages = (images: ProductImage[]) => {
    this.selectedProductImages = images;
  };
}
